// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  inject,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
} from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TranslateModule } from '@ngx-translate/core';
import { AdvancedSearchService } from '../../services/advanced-search.service';
import { AdvanceSearchResultModel } from 'src/app/api/remote-api/.portal.generated/models';
import { TableColumn } from 'src/app/_interfaces/advance-search/tableColumn';
import { ADVANCED_SEARCH_TABLE_COLUMN_CONFIG } from './advanced-search-table-columns-configuration';

@Component({
  selector: 'app-advanced-search-table',
  standalone: true,
  imports: [
    TranslateModule,
    NgIf,
    CommonModule,
    MatTableModule,
    MatSortModule,
    RouterOutlet,
  ],
  templateUrl: './advanced-search-table.component.html',
  styleUrl: './advanced-search-table.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvancedSearchTableComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  router = inject(Router);
  readonly dataSource = new MatTableDataSource<AdvanceSearchResultModel>();

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('drawerMenu') drawerMenu!: ElementRef<HTMLInputElement>;
  readonly tableColumns: TableColumn[] = ADVANCED_SEARCH_TABLE_COLUMN_CONFIG;

  readonly displayedColumns: string[] = this.tableColumns.map((col) => col.key);

  constructor(
    protected readonly advancedSearchService: AdvancedSearchService,
  ) {}

  ngOnInit(): void {
    this.setupDataSourceSubscription();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  private setupDataSourceSubscription(): void {
    this.advancedSearchService.systemList$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.dataSource.data = data ?? [];
        if (this.sort) {
          this.dataSource.sort = this.sort;
        }
      });
  }

  getSystemUrl(element: AdvanceSearchResultModel): string {
    return `/portal/system/${element.materialNumber}/${element.serialNumber}`;
  }

  getSortDirection(column: string): 'asc' | 'desc' | '' {
    if (this.sort?.active === column) {
      this.dataSource.sort = this.sort;
      return this.sort.direction as 'asc' | 'desc';
    }
    return '';
  }

  onSystemLinkClick(element: AdvanceSearchResultModel): void {
    this.advancedSearchService.createTableAnalytics(element);
  }

  getColumnConfig(columnKey: string): TableColumn | undefined {
    return this.tableColumns.find((col) => col.key === columnKey);
  }

  trackBySerialNumber(index: number, item: AdvanceSearchResultModel): string {
    return item.serialNumber ?? index.toString();
  }

  trackByColumnKey(index: number, column: TableColumn): string {
    return column.key;
  }
  routeToAdvancedSearch(): void {
    this.closeMenu();
    this.router.navigateByUrl('/advancedSearch');
  }
  closeMenu(): void {
    this.drawerMenu.nativeElement.removeAttribute('visible');
  }

  showMoreMenu = false;

  toggleMoreMenu(): void {
    this.showMoreMenu = !this.showMoreMenu;
  }

  closeMoreMenu(): void {
    this.showMoreMenu = false;
  }

  selectedStatuses: string[] = [];

  onStatusToggle(status: string): void {
    if (this.selectedStatuses.includes(status)) {
      this.selectedStatuses = this.selectedStatuses.filter((s) => s !== status);
    } else {
      this.selectedStatuses = [...this.selectedStatuses, status];
    }
  }
}
